import { supabase } from './supabase';
import { isStudioReferenceUrl, resolveReferenceImageUrl } from './referenceImageUrl';

/** Inline references so private credentials and arbitrary URL fetching stay off the server. */
export async function inlineCodexReference(value: string): Promise<string> {
    let source = value;
    if (!/^(https?:|blob:|data:)/.test(source)) {
        const header = atob(source.slice(0, 16));
        const mime = header.startsWith('\xff\xd8\xff') ? 'image/jpeg' : header.startsWith('RIFF') ? 'image/webp' : header.startsWith('GIF8') ? 'image/gif' : 'image/png';
        source = `data:${mime};base64,${source}`;
    }
    const response = await fetch(resolveReferenceImageUrl(source), { signal: AbortSignal.timeout(20_000) });
    if (!response.ok) throw new Error('Could not load the character reference image.');
    const blob = await response.blob();
    if (!blob.size || blob.size > 25 * 1024 * 1024 || !blob.type.startsWith('image/')) throw new Error('Use a reference image under 25 MB.');
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Could not read the character reference image.'));
        reader.readAsDataURL(blob);
    });
}

/** Studio asset URLs are loaded by the server, where browser CORS does not apply. */
export async function prepareCodexReference(value: string): Promise<string> {
    const normalized = resolveReferenceImageUrl(value);
    return isStudioReferenceUrl(normalized) ? normalized : inlineCodexReference(normalized);
}

export async function requestCodexText(prompt: string, references: string[] = []): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sign in to ezart to use Codex.');
    if (references.length > 16) throw new Error('Use up to 16 reference images.');
    const images: string[] = [];
    for (const reference of references) images.push(await prepareCodexReference(reference));
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-chatgpt`, {
        method: 'POST', signal: AbortSignal.timeout(150_000),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
        body: JSON.stringify({ operation: 'text', prompt, reference_images: images })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.error === 'string' ? data.error : 'Codex text request failed.');
    if (typeof data.text !== 'string' || !data.text.trim()) throw new Error('Codex returned no text.');
    return data.text;
}
